import React from 'react';
import './style.css';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const FourLinePlusBarChart = ({ lineData, barData, labels, title, deviceType, expenseColors }) => {
    // Calculate Y-axis bounds for lines
    const calculateLineYAxisBounds = () => {
        const allValues = lineData.flatMap(scenario => scenario.data);
        const minValue = Math.min(...allValues);
        const maxValue = Math.max(...allValues);
        const range = maxValue - minValue;
        const padding = range * 0.1;
        const roundTo = 1000;
        
        const min = Math.max(0, Math.floor((minValue - padding) / roundTo) * roundTo);
        const max = Math.ceil((maxValue + padding) / roundTo) * roundTo;
        const stepSize = Math.ceil((max - min) / 6 / roundTo) * roundTo;

        return { min, max, stepSize };
    };

    // Calculate Y-axis bounds for bars
    const calculateBarYAxisBounds = () => {
        const minValue = Math.min(...barData);
        const maxValue = Math.max(...barData);
        const range = maxValue - minValue;
        const padding = range * 0.1;
        const roundTo = 5000;
        
        const min = Math.max(0, Math.floor((minValue - padding) / roundTo) * roundTo);
        const max = Math.ceil((maxValue + padding) / roundTo) * roundTo;
        const stepSize = Math.ceil((max - min) / 6 / roundTo) * roundTo;

        return { min, max, stepSize };
    };

    const lineYAxis = calculateLineYAxisBounds();
    const barYAxis = calculateBarYAxisBounds();

    const getLineStyle = (index) => {
        const expense = lineData[index];
        return {
            pointRadius: 4,
            tension: 0.1,
            borderColor: expense.color,
            backgroundColor: expense.color
        };
    };

    const chartData = {
        labels,
        datasets: [
            // Bar dataset
            {
                type: 'bar',
                label: 'Total Expenses',
                data: barData,
                backgroundColor: 'rgba(198, 198, 198, 0.12)',
                borderColor: 'rgb(0, 0, 0)',
                borderWidth: 1,
                yAxisID: 'y1'
            },
            // Line datasets
            ...lineData.map((line, index) => ({
                type: 'line',
                label: line.label,
                data: line.data,
                ...getLineStyle(index),
                yAxisID: 'y'
            }))
        ]
    };

    // If deviceType is mobile then do not show the legend
    const options = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 1.75,
        plugins: {
            title: {
                display: !!title,
                text: title,
                font: { size: 12, weight: 'bold' },
                padding: { bottom: 30 }
            },
            legend: {
                display: deviceType !== 'mobile',
                position: 'bottom',
                align: 'center',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    boxWidth: 10,
                    font: { size: 12 }
                }
            },
            tooltip: {
                callbacks: {
                    label: function(context) {
                        return `${context.dataset.label}: ${new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: 'USD',
                            minimumFractionDigits: 0,
                            maximumFractionDigits: 0
                        }).format(context.parsed.y)}`;
                    }
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                position: 'left',
                min: lineYAxis.min,
                max: lineYAxis.max,
                title: {
                    display: true,
                    text: 'Cost Per Expense',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                ticks: {
                    stepSize: lineYAxis.stepSize,
                    callback: value => new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        notation: 'compact',
                        compactDisplay: 'short'
                    }).format(value)
                }
            },
            y1: {
                position: 'right',
                min: barYAxis.min,
                max: barYAxis.max,
                title: {
                    display: true,
                    text: 'Annual Total',
                    font: {
                        size: 12,
                        weight: 'bold'
                    }
                },
                grid: {
                    drawOnChartArea: false
                },
                ticks: {
                    stepSize: barYAxis.stepSize,
                    callback: value => new Intl.NumberFormat('en-US', {
                        style: 'currency',
                        currency: 'USD',
                        notation: 'compact',
                        compactDisplay: 'short'
                    }).format(value)
                }
            },
            x: {
                grid: { display: false }
            }
        }
    };

    return <Bar data={chartData} options={options} />;
};

export default FourLinePlusBarChart;
